"use client";

import { ChevronDown, Plus } from "lucide-react";
import React, { useMemo } from "react";

import { ALL_PORTFOLIO_ACCOUNTS_ID } from "@lattice/shared-frontend/charts";

import {
  DropdownMenu,
  type T_DropdownMenuItem,
} from "@lattice/frontend/components/kit/DropdownMenu";

import type { T_PortfolioHomeAccountOption } from "./types";
import "./portfolio-home.css";

const ADD_ACCOUNT_ITEMS: readonly T_DropdownMenuItem[] = [
  {
    description: "Secure, read-only sync through SnapTrade",
    id: "snaptrade",
    label: "Connect brokerage",
  },
  {
    description: "Enter and manage positions",
    id: "manual_holdings",
    label: "Add manual holdings",
  },
];

export type T_PortfolioAccountSelectorProps = {
  accounts: T_PortfolioHomeAccountOption[];
  defaultAddMenuOpen?: boolean;
  defaultScopeMenuOpen?: boolean;
  onAddManualHoldings: () => void;
  onConnectBrokerage: () => void;
  onSelect: (accountId: string) => void;
  selectedAccountId: string;
};

export function PortfolioAccountSelector({
  accounts,
  defaultAddMenuOpen = false,
  defaultScopeMenuOpen = false,
  onAddManualHoldings,
  onConnectBrokerage,
  onSelect,
  selectedAccountId,
}: T_PortfolioAccountSelectorProps) {
  const selectedAccount =
    accounts.find((account) => account.id === selectedAccountId) ?? accounts[0];
  const triggerLabel =
    selectedAccount?.id === ALL_PORTFOLIO_ACCOUNTS_ID
      ? "All"
      : (selectedAccount?.label ?? "All");
  const scopeItems = useMemo<T_DropdownMenuItem[]>(
    () =>
      accounts.map((account) => ({
        description: account.description,
        id: account.id,
        label: account.label,
        selected: account.id === selectedAccountId,
      })),
    [accounts, selectedAccountId],
  );

  return (
    <div className="portfolio-account-selector">
      <DropdownMenu
        defaultOpen={defaultScopeMenuOpen}
        items={scopeItems}
        menuAccessibilityLabel="Portfolio scope"
        menuWidth={292}
        onSelect={(item) => onSelect(item.id)}
        renderTrigger={({ isOpen }) => (
          <span className="portfolio-account-selector__scope">
            <span className="portfolio-account-selector__label">
              {triggerLabel}
            </span>
            <ChevronDown
              aria-hidden
              className={[
                "portfolio-account-selector__chevron",
                isOpen ? "portfolio-account-selector__chevron--open" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              size={18}
              strokeWidth={2.5}
            />
          </span>
        )}
        searchable={accounts.length > 6}
        searchPlaceholder="Search accounts"
        triggerAccessibilityLabel="Choose portfolio account"
      />
      <span className="portfolio-account-selector__add-menu">
        <DropdownMenu
          defaultOpen={defaultAddMenuOpen}
          items={ADD_ACCOUNT_ITEMS}
          menuAccessibilityLabel="Add portfolio account"
          menuWidth={292}
          onSelect={(item) => {
            if (item.id === "manual_holdings") onAddManualHoldings();
            if (item.id === "snaptrade") onConnectBrokerage();
          }}
          renderTrigger={({ isOpen }) => (
            <Plus
              aria-hidden
              className={[
                "portfolio-account-selector__add",
                isOpen ? "portfolio-account-selector__add--open" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              size={22}
              strokeWidth={2.5}
            />
          )}
          triggerAccessibilityLabel="Add portfolio account"
        />
      </span>
    </div>
  );
}
